import {create, useStore} from 'zustand';
import {useSaveIntentModel} from '../../hooks/api/intent';
import {useMutation} from '@tanstack/react-query';
import {useIntentModelFromContext} from './IntentModelProvider';
import {useEffect} from 'react';
import {IntentModel} from '../../shared/customer';
import {AccountFieldOrCustomField} from '../../shared/signalDefinition';
import {ObjectId} from 'bson';
import _ from 'lodash';
import {IntentSource, IntentSourceLabels} from '../../shared/intent';
import {filterNull, keys} from '../../shared/util';
import {useToast} from '@chakra-ui/react';
import {ValidationError} from '../../lib/errors';
import {produce} from 'immer';
import {FieldDefinition} from '../../shared/fields/types';
import {isIntentFieldDefinition} from '../../shared/fields/intent';
import {useCustomer} from '../../hooks/api/metadata';

export type IntentGroup = IntentSource['type'] | 'linkedIn';

export const IntentGroupLabels = {
  ...IntentSourceLabels,
  linkedIn: 'LinkedIn Ads',
} as const satisfies Record<IntentGroup, string>;
export const IntentGroups = keys(IntentGroupLabels);

export type IntentSignalDisplayField =
  | {
      type: 'placeholder';
      id: ObjectId;
      group: IntentGroup;
    }
  | {
      type: 'field';
      id: AccountFieldOrCustomField;
      group: IntentGroup;
      weight?: number;
    };

interface IntentModelStore {
  hasInitialized: boolean;
  initialize: (
    intentModel: IntentModel,
    fieldDefinitions: FieldDefinition[],
    intentSources: IntentSource[]
  ) => void;

  thresholds: {low?: number; medium?: number; high?: number};
  setThreshold: (threshold: 'low' | 'medium' | 'high', value?: number) => void;

  displayFields: IntentSignalDisplayField[];
  addPlaceholderField: (group: IntentGroup) => void;
  removeField: (id: AccountFieldOrCustomField) => void;
  removeGroup: (group: IntentGroup) => void;
  updateField: (
    id: AccountFieldOrCustomField,
    field: IntentSignalDisplayField
  ) => void;
}

function getDisplayFieldsFromIntentModel(
  intentModel: IntentModel,
  fieldDefinitions: FieldDefinition[],
  intentSources: IntentSource[]
): IntentSignalDisplayField[] {
  return filterNull(
    intentModel.fieldsWithWeights.map((fieldWithWeight) => {
      const fieldDefinition = fieldDefinitions.find(({id}) =>
        id.equals(fieldWithWeight.field)
      );

      if (!fieldDefinition || !isIntentFieldDefinition(fieldDefinition)) {
        return null;
      }

      let group: IntentGroup | undefined;
      if (fieldDefinition.source === 'linkedIn') {
        group = 'linkedIn' as const;
      } else if (fieldDefinition.source === 'aggregated') {
        const intentSource = intentSources.find((source) =>
          source.id.equals(fieldDefinition.sourceId)
        );
        group = intentSource?.type;
      }

      return group
        ? {
            id: fieldWithWeight.field,
            weight: fieldWithWeight.weight,
            type: 'field',
            group,
          }
        : null;
    })
  );
}

const intentModelStore = create<IntentModelStore>((set, get) => ({
  hasInitialized: false,
  initialize: (
    intentModel: IntentModel,
    fieldDefinitions: FieldDefinition[],
    intentSources: IntentSource[]
  ) => {
    if (get().hasInitialized) {
      return;
    }

    set({
      hasInitialized: true,
      thresholds: intentModel.thresholds,
      displayFields: getDisplayFieldsFromIntentModel(
        intentModel,
        fieldDefinitions,
        intentSources
      ),
    });
  },

  thresholds: {},
  setThreshold: (threshold, value) =>
    set((state) => ({
      thresholds: {
        ...state.thresholds,
        [threshold]: value,
      },
    })),

  displayFields: [],
  addPlaceholderField: (group) =>
    set((state) => ({
      displayFields: [
        ...state.displayFields,
        {type: 'placeholder', group, id: new ObjectId()},
      ],
    })),
  removeField: (id) =>
    set((state) => ({
      displayFields: state.displayFields.filter((f) => !_.isEqual(f.id, id)),
    })),
  removeGroup: (group) =>
    set((state) => ({
      displayFields: state.displayFields.filter((f) => f.group !== group),
    })),
  updateField: (id, field) =>
    set(
      produce<IntentModelStore>((state) => {
        const index = state.displayFields.findIndex((f) => _.isEqual(f.id, id));
        if (index !== -1) {
          state.displayFields[index] = field;
        }
      })
    ),
}));

export const useIntentModelStore = () => {
  const intentModel = useIntentModelFromContext();
  const customer = useCustomer();
  const store = useStore(intentModelStore);
  const saveIntentModelMutation = useSaveIntentModel();
  const toast = useToast();

  const {initialize} = store;
  useEffect(() => {
    initialize(
      intentModel,
      customer.fieldDefinitions ?? [],
      customer.intentSources ?? []
    );
  }, [
    intentModel,
    customer.fieldDefinitions,
    customer.intentSources,
    initialize,
  ]);

  const saveIntentModel = useMutation({
    mutationFn: () => {
      const {low, medium, high} = store.thresholds;
      if (low === undefined || medium === undefined || high === undefined) {
        throw new ValidationError('All thresholds are required');
      }

      const fieldsWithWeights = filterNull(
        store.displayFields.map((displayField) => {
          if (
            displayField.type === 'placeholder' ||
            displayField.weight === undefined
          ) {
            return null;
          }

          return {
            field: displayField.id,
            weight: displayField.weight,
          };
        })
      );

      return saveIntentModelMutation.mutateAsync({
        id: intentModel.id,
        thresholds: {low, medium, high},
        fieldsWithWeights,
      });
    },
    onError: (error) => {
      // Server error toasts are handled by the API layer
      if (!(error instanceof ValidationError)) {
        throw error;
      }

      toast({
        title: 'Invalid intent model',
        description: error.message,
        duration: 2_000,
        position: 'bottom-left',
        status: 'error',
      });
    },
  });

  return {
    ...store,
    saveIntentModel,
  };
};
